import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useExtendBooking } from '@/api/hooks/useBookings';
import { formatCurrency } from '@/lib/format';
import type { AppError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

type ExtendDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  extensionMinutes: number;
  extensionPrice: number;
  creditBalance: number;
  currency: string;
};

export default function ExtendDialog({
  open,
  onOpenChange,
  bookingId,
  extensionMinutes,
  extensionPrice,
  creditBalance,
  currency,
}: ExtendDialogProps) {
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const extendMutation = useExtendBooking();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const creditToApply = Math.min(creditBalance, extensionPrice);
  const amountDue = extensionPrice - creditToApply;

  const handleExtend = async () => {
    setError(null);
    setProcessing(true);

    try {
      let paymentMethodId: string | undefined;

      if (amountDue > 0) {
        if (!stripe || !elements) {
          setError(t('common.error'));
          setProcessing(false);
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          setError(t('common.error'));
          setProcessing(false);
          return;
        }

        const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (stripeError) {
          setError(stripeError.message || t('common.error'));
          setProcessing(false);
          return;
        }

        paymentMethodId = paymentMethod.id;
      }

      await extendMutation.mutateAsync({
        id: bookingId,
        additionalMinutes: extensionMinutes,
        paymentMethodId,
      });

      onOpenChange(false);
      toast.success(t('session.extend_success', { minutes: extensionMinutes }));
    } catch (err) {
      const appErr = err as AppError;
      if (appErr.code === 'SLOT_UNAVAILABLE') {
        setError(t('session.extend_slot_unavailable'));
      } else if (appErr.code === 'EXTENSION_EXCEEDS_MAX') {
        setError(t('session.extend_max_reached'));
      } else if (appErr.code === 'EQUIPMENT_UNAVAILABLE') {
        setError(t('session.extend_addon_unavailable'));
      } else if (appErr.code === 'PAYMENT_FAILED') {
        setError(t('booking.card_declined'));
      } else {
        setError(appErr.message || t('common.error'));
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('session.extend_session')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Pricing breakdown */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>{t('session.extend_by', { minutes: extensionMinutes })}</span>
              <span className="font-mono">{formatCurrency(extensionPrice, currency, i18n.language)}</span>
            </div>

            {creditToApply > 0 && (
              <div className="flex justify-between text-green-600">
                <span>{t('credit.credit_applied')}</span>
                <span className="font-mono">-{formatCurrency(creditToApply, currency, i18n.language)}</span>
              </div>
            )}

            <Separator />

            <div className="flex justify-between font-semibold">
              <span>{t('credit.amount_due')}</span>
              <span className="font-mono">{formatCurrency(amountDue, currency, i18n.language)}</span>
            </div>
          </div>

          {/* Stripe card form — only if payment needed */}
          {amountDue > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('checkout.pay_with_card')}</p>
              <div className="rounded-md border p-3">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#1a1a1a',
                        '::placeholder': { color: '#a3a3a3' },
                      },
                    },
                    hidePostalCode: true,
                  }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleExtend} disabled={processing || (amountDue > 0 && !stripe)}>
            {processing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {amountDue > 0
              ? t('session.pay_and_extend', { amount: formatCurrency(amountDue, currency, i18n.language) })
              : t('session.confirm_extend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
